// Numeric-quantity bottom sheet — replaces `window.prompt()` for the
// "how many of these items?" interaction. The browser's native prompt
// is the worst possible mobile UX (unstyled OS modal, breaks the
// cinematic moment, no validation feedback, no max button). This sheet
// gives a stepper, free numeric input, and a Max shortcut.
//
// Three current callers (Session 7 conversion targets):
//   - promptTransferToTable (inventory → crafting table)
//   - promptDepositToParty  (inventory → shared party pool)
//   - promptTakeFromParty   (party pool → inventory)
//
// Design notes:
//   - The numeric <input> uses `inputMode="numeric"` + `type="number"`
//     so iOS / Android raise the numeric keypad, not the alpha keyboard.
//   - Defaults to `max` (full quantity) because every existing prompt
//     pre-filled with the full quantity — user expectation is "take all"
//     by default, override down for partials.
//   - Confirm button label is dynamic ("Transfer 5") so the user sees
//     the action+quantity before tapping, not generic "Confirm".
//   - Boundary buttons disable rather than silently no-op, so the
//     state is legible.

import React, { useEffect, useState } from 'react';
import MobileBottomSheet from './MobileBottomSheet';

export default function PartialQtySheet({
  open,
  onClose,
  onConfirm,
  title = 'Adjust Quantity',
  max,
  min = 1,
  initialValue = null,
  label = 'Quantity',
  actionLabel = null, // e.g., 'Transfer', 'Deposit', 'Take' — qty appended at render time
}) {
  const safeMax = Number.isFinite(max) ? Math.max(min, max) : min;
  const [value, setValue] = useState(() => {
    if (Number.isFinite(initialValue)) return Math.min(safeMax, Math.max(min, initialValue));
    return safeMax;
  });

  // Reset value on each open so a stale leftover from a previous open
  // doesn't render. Keyed on `open` to avoid resetting during user typing.
  useEffect(() => {
    if (!open) return;
    if (Number.isFinite(initialValue)) {
      setValue(Math.min(safeMax, Math.max(min, initialValue)));
    } else {
      setValue(safeMax);
    }
  }, [open, initialValue, safeMax, min]);

  const valid = Number.isFinite(value) && value >= min && value <= safeMax;

  const handleConfirm = () => {
    if (!valid || !onConfirm) return;
    onConfirm(value);
    onClose();
  };

  const handleStep = (delta) => {
    setValue((prev) => {
      const next = (Number.isFinite(prev) ? prev : min) + delta;
      return Math.min(safeMax, Math.max(min, next));
    });
  };

  const handleInput = (e) => {
    const raw = e.target.value;
    if (raw === '') {
      setValue('');
      return;
    }
    const n = parseInt(raw, 10);
    if (Number.isFinite(n)) {
      setValue(Math.min(safeMax, Math.max(min, n)));
    }
  };

  const handleBlur = () => {
    if (!Number.isFinite(value)) setValue(min);
  };

  // Confirm label shows "Transfer 5" rather than generic "Confirm". When
  // the input is mid-edit (empty string), fall back to safeMax so the
  // button doesn't flicker to a number-less "Transfer" — the button is
  // disabled in that state anyway (valid=false), so safeMax is a stable
  // visual placeholder, not a misleading commit promise.
  const labelQty = Number.isFinite(value) ? value : safeMax;
  const confirmText = actionLabel ? `${actionLabel} ${labelQty}` : 'Confirm';

  return (
    <MobileBottomSheet open={open} onClose={onClose} ariaLabel={title}>
      <div style={{ padding: '6px 8px 14px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
        {/* Title */}
        <div style={{
          fontSize: '1rem',
          fontWeight: 600,
          color: 'var(--tp, #e8ddd0)',
          borderBottom: '1px solid var(--bs, #2a2033)',
          paddingBottom: '10px',
        }}>
          {title}
        </div>

        {/* Label */}
        <div style={{
          fontSize: '0.7rem',
          color: 'var(--ts, #9a8b7a)',
          textTransform: 'uppercase',
          letterSpacing: '1.2px',
          fontWeight: 600,
        }}>
          {label}
        </div>

        {/* Stepper row */}
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <button
            type="button"
            onClick={() => handleStep(-1)}
            disabled={!Number.isFinite(value) || value <= min}
            aria-label="Decrease quantity"
            style={{
              minWidth: '48px',
              minHeight: '48px',
              background: 'rgba(212,168,50,0.15)',
              border: '1px solid rgba(212,168,50,0.4)',
              color: 'var(--tg, #d4a832)',
              borderRadius: '6px',
              fontSize: '1.2rem',
              fontWeight: 700,
              cursor: 'pointer',
              opacity: (!Number.isFinite(value) || value <= min) ? 0.4 : 1,
            }}
          >
            −
          </button>

          <input
            type="number"
            inputMode="numeric"
            min={min}
            max={safeMax}
            value={value === '' ? '' : value}
            onChange={handleInput}
            onBlur={handleBlur}
            aria-label={label}
            style={{
              flex: 1,
              minHeight: '48px',
              padding: '10px 12px',
              background: 'var(--bg1, #16101a)',
              border: '1px solid var(--bs, #2a2033)',
              borderRadius: '6px',
              color: 'var(--tg, #d4a832)',
              fontSize: '1.2rem',
              fontWeight: 700,
              textAlign: 'center',
              fontVariantNumeric: 'tabular-nums',
            }}
          />

          <button
            type="button"
            onClick={() => handleStep(1)}
            disabled={!Number.isFinite(value) || value >= safeMax}
            aria-label="Increase quantity"
            style={{
              minWidth: '48px',
              minHeight: '48px',
              background: 'rgba(212,168,50,0.15)',
              border: '1px solid rgba(212,168,50,0.4)',
              color: 'var(--tg, #d4a832)',
              borderRadius: '6px',
              fontSize: '1.2rem',
              fontWeight: 700,
              cursor: 'pointer',
              opacity: (!Number.isFinite(value) || value >= safeMax) ? 0.4 : 1,
            }}
          >
            +
          </button>
        </div>

        {/* Max + range hint row */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
          <button
            type="button"
            onClick={() => setValue(safeMax)}
            aria-label={`Set quantity to maximum (${safeMax})`}
            style={{
              minHeight: '36px',
              padding: '6px 12px',
              background: 'rgba(125,216,125,0.14)',
              border: '1px solid rgba(125,216,125,0.4)',
              color: 'var(--tgr, #7dd87d)',
              borderRadius: '4px',
              fontSize: '0.78rem',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Max ({safeMax})
          </button>
          <span style={{ fontSize: '0.7rem', color: 'var(--tm, #5e5266)' }}>
            Valid: {min}–{safeMax}
          </span>
        </div>

        {/* Action row */}
        <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              flex: '1 1 100%',
              minHeight: '44px',
              padding: '10px',
              background: 'rgba(255,255,255,0.04)',
              border: '1px solid var(--bs, #2a2033)',
              color: 'var(--tp, #e8ddd0)',
              borderRadius: '4px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!valid}
            style={{
              flex: '1 1 100%',
              minHeight: '44px',
              padding: '10px',
              background: valid ? 'linear-gradient(180deg, #4a9b2e 0%, #2e5c1f 100%)' : 'rgba(74,155,46,0.3)',
              border: `1px solid ${valid ? '#7dd87d' : 'rgba(125,216,125,0.3)'}`,
              color: '#fff',
              borderRadius: '4px',
              fontWeight: 700,
              cursor: valid ? 'pointer' : 'not-allowed',
              opacity: valid ? 1 : 0.6,
            }}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </MobileBottomSheet>
  );
}
